import React, { useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useAdminCreateSupplyBeamLocationMutation,
  useAdminUpdateSupplyBeamLocationMutation,
} from "../../../features/admin/supplyBeamLocations/supplyBeamLocationsApi";
import { useAdminGetSupplyBeamConfigListMutation } from "../../../features/admin/supplyBeamConfig/supplyBeamConfigApi";
import { MdLocationOn } from "react-icons/md";
import { FiArrowLeft } from "react-icons/fi";

const DEFAULT_LOCATION_TYPES = [
  { value: "Plant", label: "Plant / Factory" },
  { value: "Warehouse", label: "Warehouse" },
  { value: "Depot", label: "Regional Depot" },
  { value: "Distributor", label: "Distributor" },
  { value: "Retailer", label: "Retailer" },
];

const emptyForm = {
  location: "",
  short_name: "",
  address: "",
  country: "India",
  state: "",
  district: "",
  city: "",
  location_type: "",
  location_type_id: null,
  location_code: "",
  geofence_per_location: "",
  capacity_tracking: "",
};

const AddEditSupplyBeamLocation = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const token = sessionStorage.getItem("access_token");

  const stateData = routeLocation?.state ?? null;

  // Determine edit vs add
  // Edit: state has an `id` field (location id) and a `tenantInfo` sub-object
  // Add: state has only `tenantInfo`
  const isEdit = !!stateData?.id;
  const tenantInfo = stateData?.tenantInfo ?? null;
  const editData = isEdit ? stateData : null;

  const [form, setForm] = useState(() => {
    if (isEdit) {
      return {
        location: editData.location ?? "",
        short_name: editData.short_name ?? "",
        address: editData.address ?? "",
        country: editData.country ?? "India",
        state: editData.state ?? "",
        district: editData.district ?? "",
        city: editData.city ?? "",
        location_type: editData.location_type ?? "",
        location_code: editData.location_code ?? "",
        geofence_per_location: editData.geofence_per_location ?? "",
        capacity_tracking: editData.capacity_tracking ?? "",
      };
    }
    return emptyForm;
  });

  const [
    createLocation,
    { data: createData, isLoading: createLoading, error: createError },
  ] = useAdminCreateSupplyBeamLocationMutation();

  const [
    updateLocation,
    { data: updateData, isLoading: updateLoading, error: updateError },
  ] = useAdminUpdateSupplyBeamLocationMutation();

  const [
    fetchConfigs,
    { data: configsData },
  ] = useAdminGetSupplyBeamConfigListMutation();

  React.useEffect(() => {
    if (tenantInfo?.tenant_id) {
      fetchConfigs({
        token,
        tenant_id: tenantInfo.tenant_id,
        query: "status=1",
      });
    }
  }, [tenantInfo]);

  const configuredStages = configsData?.body ?? [];

  useMemo(() => {
    if (createData) {
      toast.dismiss();
      toast.success(createData.message ?? "Location created successfully!", {
        id: "createOk",
      });
      navigate(-1);
    }
    if (createError) {
      toast.dismiss();
      toast.error(createError?.data?.message ?? "Failed to create location", {
        id: "createErr",
      });
    }
  }, [createData, createError]);

  useMemo(() => {
    if (updateData) {
      toast.dismiss();
      toast.success(updateData.message ?? "Location updated successfully!", {
        id: "updateOk",
      });
      navigate(-1);
    }
    if (updateError) {
      toast.dismiss();
      toast.error(updateError?.data?.message ?? "Failed to update location", {
        id: "updateErr",
      });
    }
  }, [updateData, updateError]);

  const handleChange = (field) => (e) =>
    setForm((p) => ({ ...p, [field]: e.target.value }));

  const validate = () => {
    const required = [
      ["location", "Location name"],
      ["address", "Address"],
      ["country", "Country"],
      ["state", "State"],
      ["district", "District"],
      ["city", "City"],
    ];
    for (const [field, label] of required) {
      if (!form[field]?.trim()) {
        toast.error(`${label} is required`, { id: "validErr" });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (!tenantInfo?.tenant_id) {
      toast.error("Tenant information missing. Please go back and try again.");
      return;
    }

    const body = {
      location: form.location.trim(),
      short_name: form.short_name.trim() || null,
      address: form.address.trim(),
      country: form.country.trim(),
      state: form.state.trim(),
      district: form.district.trim(),
      city: form.city.trim(),
      location_type: form.location_type || null,
      location_type_id: form.location_type_id ? Number(form.location_type_id) : null,
      location_code: form.location_code.trim() || null,
      geofence_per_location: form.geofence_per_location
        ? Number(form.geofence_per_location)
        : null,
      capacity_tracking: form.capacity_tracking
        ? Number(form.capacity_tracking)
        : null,
    };

    if (isEdit) {
      updateLocation({
        token,
        tenant_id: tenantInfo.tenant_id,
        id: editData.id,
        body,
      });
    } else {
      createLocation({
        token,
        tenant_id: tenantInfo.tenant_id,
        body,
      });
    }
  };

  const isLoading = createLoading || updateLoading;

  const SectionHeading = ({ label }) => (
    <h6
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#64748b",
        borderBottom: "1px solid #e9ecef",
        paddingBottom: 6,
        marginBottom: 16,
      }}
    >
      {label}
    </h6>
  );

  return (
    <div className="page-content">
      <div className="row">
        <div className="col-lg-10 mx-auto">
          <div className="card">
            <div className="card-body">
              {/* Back */}
              <div className="mb-3">
                <button
                  className="btn btn-link p-0 text-decoration-none text-muted d-flex align-items-center gap-1"
                  style={{ fontSize: 13 }}
                  onClick={() => navigate(-1)}
                >
                  <FiArrowLeft size={14} />
                  Back to {tenantInfo?.name ?? "Locations"}
                </button>
              </div>

              {/* Header */}
              <div className="borderbtm mb-4 d-flex align-items-center gap-3">
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background:
                      "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <MdLocationOn size={20} color="#fff" />
                </div>
                <div>
                  <h4 className="card-title mb-0">
                    {isEdit ? "Edit" : "Add"} Location —{" "}
                    <span style={{ color: "#4f46e5" }}>
                      {tenantInfo?.name ?? ""}
                    </span>
                  </h4>
                  <p className="text-muted mb-0" style={{ fontSize: 12 }}>
                    Tenant ID: {tenantInfo?.tenant_id} · {tenantInfo?.slug}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Basic Info */}
                <div className="mb-4">
                  <SectionHeading label="Basic Information" />
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label fw-semibold">
                        Location Name <span className="text-danger">*</span>
                      </label>
                      <input
                        id="sb-location-name"
                        type="text"
                        className="form-control"
                        placeholder="e.g. Mumbai Warehouse"
                        value={form.location}
                        onChange={handleChange("location")}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-semibold">
                        Short Name
                      </label>
                      <input
                        id="sb-short-name"
                        type="text"
                        className="form-control"
                        placeholder="e.g. MUM-WH"
                        value={form.short_name}
                        onChange={handleChange("short_name")}
                      />
                    </div>
                    <div className="col-md-2 mb-3">
                      <label className="form-label fw-semibold">
                        Location Code
                      </label>
                      <input
                        id="sb-location-code"
                        type="text"
                        className="form-control"
                        placeholder="e.g. LOC-001"
                        value={form.location_code}
                        onChange={handleChange("location_code")}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-semibold">
                        Location Stage / Type
                      </label>
                      <select
                        id="sb-location-type"
                        className="form-control"
                        value={form.location_type_id ? String(form.location_type_id) : form.location_type}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (!val) {
                            setForm((prev) => ({ ...prev, location_type: "", location_type_id: null }));
                            return;
                          }
                          const found = configuredStages.find((s) => String(s.id) === val || s.location_type === val);
                          if (found) {
                            setForm((prev) => ({
                              ...prev,
                              location_type: found.location_type,
                              location_type_id: found.id,
                              geofence_per_location: prev.geofence_per_location || (found.geofence_radius ? String(found.geofence_radius) : ""),
                              capacity_tracking: prev.capacity_tracking || (found.max_capacity ? String(found.max_capacity) : ""),
                            }));
                          } else {
                            setForm((prev) => ({ ...prev, location_type: val, location_type_id: null }));
                          }
                        }}
                      >
                        <option value="">— Select Stage / Type —</option>
                        {configuredStages.length > 0
                          ? configuredStages.map((st) => (
                              <option key={st.id} value={st.id}>
                                #{st.order_index} {st.location_type}
                              </option>
                            ))
                          : DEFAULT_LOCATION_TYPES.map((lt) => (
                              <option key={lt.value} value={lt.value}>
                                {lt.label}
                              </option>
                            ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="mb-4">
                  <SectionHeading label="Address Details" />
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-semibold">
                        Full Address <span className="text-danger">*</span>
                      </label>
                      <textarea
                        id="sb-address"
                        className="form-control"
                        rows={2}
                        placeholder="Street, Building, Landmark..."
                        value={form.address}
                        onChange={handleChange("address")}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-semibold">
                        Country <span className="text-danger">*</span>
                      </label>
                      <input
                        id="sb-country"
                        type="text"
                        className="form-control"
                        placeholder="Country"
                        value={form.country}
                        onChange={handleChange("country")}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-semibold">
                        State <span className="text-danger">*</span>
                      </label>
                      <input
                        id="sb-state"
                        type="text"
                        className="form-control"
                        placeholder="State"
                        value={form.state}
                        onChange={handleChange("state")}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-semibold">
                        District <span className="text-danger">*</span>
                      </label>
                      <input
                        id="sb-district"
                        type="text"
                        className="form-control"
                        placeholder="District"
                        value={form.district}
                        onChange={handleChange("district")}
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-semibold">
                        City <span className="text-danger">*</span>
                      </label>
                      <input
                        id="sb-city"
                        type="text"
                        className="form-control"
                        placeholder="City"
                        value={form.city}
                        onChange={handleChange("city")}
                      />
                    </div>
                  </div>
                </div>

                {/* Advanced Config */}
                <div className="mb-4">
                  <SectionHeading label="Advanced Configuration" />
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-semibold">
                        Geofence Radius (meters)
                      </label>
                      <input
                        id="sb-geofence"
                        type="number"
                        min="0"
                        className="form-control"
                        placeholder="e.g. 500"
                        value={form.geofence_per_location}
                        onChange={handleChange("geofence_per_location")}
                      />
                      <small className="text-muted">
                        Radius in metres for check-in geofencing
                      </small>
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label fw-semibold">
                        Capacity Tracking
                      </label>
                      <input
                        id="sb-capacity"
                        type="number"
                        min="0"
                        className="form-control"
                        placeholder="e.g. 1000"
                        value={form.capacity_tracking}
                        onChange={handleChange("capacity_tracking")}
                      />
                      <small className="text-muted">
                        Max units this location can hold
                      </small>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="d-flex gap-2 mt-2">
                  <button
                    id="sb-location-submit"
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? isEdit
                        ? "Updating..."
                        : "Creating..."
                      : isEdit
                      ? "Update Location"
                      : "Create Location"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(-1)}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditSupplyBeamLocation;
